import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';

const CollectionsPanel = () => {
  const [collections, setCollections] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const coverPreviewRef = useRef(null);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      const data = await api.getCollections();
      setCollections(data);
    } catch (err) {
      console.error('Failed to load collections:', err);
    }
  };

  const handleNew = () => {
    setCurrentCollection({
      couple: '',
      location: '',
      cover_image_url: '',
      cover_focal_point: { x: 50, y: 50 },
      images: [],
      tags: [],
      type: 'photo',
    });
    setIsEditing(true);
  };

  const handleEdit = (collection) => {
    // Normalize images to objects with url and order
    const normalizedImages = (collection.images || []).map((img, idx) => {
      if (typeof img === 'string') {
        return { url: img, order: idx };
      }
      return { ...img, order: img.order ?? idx };
    });
    
    setCurrentCollection({
      ...collection,
      images: normalizedImages,
      tags: collection.tags || [],
      cover_focal_point: collection.cover_focal_point || { x: 50, y: 50 }
    });
    setIsEditing(true);
  };

  const handleImageUpload = async (e, isCover = false) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    setMessage('');

    try {
      if (isCover) {
        const result = await api.uploadImage(files[0], 'collections');
        setCurrentCollection({ 
          ...currentCollection, 
          cover_image_url: result.url,
          cover_focal_point: { x: 50, y: 50 }
        });
      } else {
        const result = await api.uploadMultipleImages(files, 'collections');
        const currentMaxOrder = currentCollection.images.length > 0 
          ? Math.max(...currentCollection.images.map(img => img.order)) 
          : -1;
        
        const newImages = result.uploaded.map((u, idx) => ({
          url: u.url,
          order: currentMaxOrder + 1 + idx,
          filename: u.filename
        }));
        
        setCurrentCollection({
          ...currentCollection,
          images: [...currentCollection.images, ...newImages],
        });
      }
      setMessage('Images uploaded successfully!');
    } catch (err) {
      setMessage('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = [...currentCollection.images];
    newImages.splice(index, 1);
    // Re-order remaining images
    const reordered = newImages.map((img, idx) => ({ ...img, order: idx }));
    setCurrentCollection({ ...currentCollection, images: reordered });
  };

  // Drag and drop for reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newImages = [...currentCollection.images];
    const draggedItem = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedItem);
    
    // Update orders
    const reordered = newImages.map((img, idx) => ({ ...img, order: idx }));
    
    setCurrentCollection({ ...currentCollection, images: reordered });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Cover image focal point
  const handleCoverClick = (e) => {
    if (!coverPreviewRef.current) return;
    
    const rect = coverPreviewRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    
    setCurrentCollection({
      ...currentCollection,
      cover_focal_point: { x, y }
    });
  };

  const handleSave = async () => {
    try {
      // Prepare data for saving
      const dataToSave = {
        ...currentCollection,
        // Convert images to the format expected by backend
        images: currentCollection.images.map(img => ({
          url: img.url,
          order: img.order,
          filename: img.filename || '',
          tags: img.tags || [],
        }))
      };

      if (currentCollection.id) {
        await api.updateCollection(currentCollection.id, dataToSave);
      } else {
        await api.createCollection(dataToSave);
      }
      setMessage('Collection saved!');
      setIsEditing(false);
      loadCollections();
    } catch (err) {
      setMessage('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collection?')) return;

    try {
      await api.deleteCollection(id);
      loadCollections();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  // Move image up/down in order
  const moveImage = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= currentCollection.images.length) return;
    
    const newImages = [...currentCollection.images];
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    
    // Update orders
    const reordered = newImages.map((img, idx) => ({ ...img, order: idx }));
    setCurrentCollection({ ...currentCollection, images: reordered });
  };

  if (isEditing) {
    const focalX = currentCollection.cover_focal_point?.x ?? 50;
    const focalY = currentCollection.cover_focal_point?.y ?? 50;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            {currentCollection.id ? 'Edit Collection' : 'New Collection'}
          </h2>
          <button
            onClick={() => setIsEditing(false)}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>

        {message && (
          <div className={`p-4 rounded ${message.includes('success') || message.includes('saved') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {message}
          </div>
        )}

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Couple Name</label>
              <input
                type="text"
                value={currentCollection.couple}
                onChange={(e) => setCurrentCollection({ ...currentCollection, couple: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="e.g., Sarah & James"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Location</label>
              <input
                type="text"
                value={currentCollection.location}
                onChange={(e) => setCurrentCollection({ ...currentCollection, location: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="e.g., Toronto, Canada"
              />
            </div>
          </div>

          {/* Collection Tags */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium mb-2">Collection Tags</label>
            <p className="text-xs text-gray-500 mb-3">Click existing tags or type new ones. Tags help categorize and filter collections.</p>
            
            {/* Existing tags on this collection */}
            {(currentCollection.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {currentCollection.tags.map((tag, i) => (
                  <span key={i} className="px-2.5 py-1 bg-black text-white text-xs rounded-full flex items-center gap-1">
                    {tag}
                    <button onClick={() => {
                      const newTags = [...currentCollection.tags];
                      newTags.splice(i, 1);
                      setCurrentCollection({ ...currentCollection, tags: newTags });
                    }} className="ml-0.5 hover:text-red-300 text-sm leading-none">&times;</button>
                  </span>
                ))}
              </div>
            )}

            {/* Suggested tags from all collections */}
            {(() => {
              const usedTags = new Set();
              collections.forEach(c => (c.tags || []).forEach(t => usedTags.add(t)));
              const suggestions = [...usedTags].filter(t => !(currentCollection.tags || []).includes(t)).sort();
              if (suggestions.length === 0) return null;
              return (
                <div className="mb-3">
                  <p className="text-[10px] tracking-wider uppercase text-gray-400 mb-1.5">Click to add:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setCurrentCollection({ ...currentCollection, tags: [...(currentCollection.tags || []), tag] })}
                        className="px-2.5 py-1 border border-dashed border-gray-300 text-xs rounded-full text-gray-500 hover:border-black hover:text-black transition-colors"
                        data-testid={`tag-suggestion-${tag}`}
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Add new tag input */}
            <div className="flex gap-2">
              <input
                type="text"
                className="flex-1 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black text-sm"
                placeholder="Type a new tag and press Enter"
                data-testid="collection-tags-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    const val = e.target.value.trim();
                    if (val && !(currentCollection.tags || []).includes(val)) {
                      setCurrentCollection({ ...currentCollection, tags: [...(currentCollection.tags || []), val] });
                    }
                    e.target.value = '';
                  }
                }}
              />
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Press Enter or comma to add a new tag</p>
          </div>

          {/* Cover Image with Focal Point */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium mb-2">Cover Image</label>
            <p className="text-xs text-gray-500 mb-3">Click on the image to set the focal point for cropping</p>
            
            {currentCollection.cover_image_url && (
              <div className="mb-4">
                <div 
                  ref={coverPreviewRef}
                  className="relative w-full max-w-lg cursor-crosshair border border-gray-300 rounded overflow-hidden"
                  onClick={handleCoverClick}
                >
                  <img
                    src={currentCollection.cover_image_url}
                    alt="Cover"
                    className="w-full"
                  />
                  {/* Focal point indicator */}
                  <div 
                    className="absolute w-6 h-6 border-2 border-white rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ 
                      left: `${focalX}%`, 
                      top: `${focalY}%`,
                      backgroundColor: 'rgba(0,0,0,0.5)'
                    }}
                  >
                    <div className="absolute inset-1 bg-white rounded-full" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Focal point: {focalX}%, {focalY}%
                </p>
                
                {/* Preview of how it will look cropped */}
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-700 mb-2">Preview (how hero will crop):</p>
                  <div className="w-full max-w-lg h-32 overflow-hidden rounded border border-gray-300">
                    <img
                      src={currentCollection.cover_image_url}
                      alt="Cover preview"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: `${focalX}% ${focalY}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
            
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, true)}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
            />
          </div>

          {/* Gallery Images with Drag & Drop */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium mb-2">Gallery Images</label>
            <p className="text-xs text-gray-500 mb-3">Drag to reorder or use arrows. First image appears first in the gallery.</p>
            
            {currentCollection.images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
                {currentCollection.images
                  .sort((a, b) => a.order - b.order)
                  .map((img, index) => (
                  <div 
                    key={img.url + index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`relative group bg-white rounded shadow-sm overflow-hidden ${
                      draggedIndex === index ? 'opacity-50 ring-2 ring-blue-500' : ''
                    }`}
                  >
                    <div className="aspect-square cursor-move">
                      <img 
                        src={img.url} 
                        alt="" 
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    </div>
                    
                    {/* Order badge */}
                    <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                      #{index + 1}
                    </div>

                    {/* Tag badges */}
                    {(img.tags || []).length > 0 && (
                      <div className="absolute bottom-8 left-1 right-1 flex flex-wrap gap-0.5">
                        {img.tags.slice(0, 2).map((t, ti) => (
                          <span key={ti} className="bg-blue-500/80 text-white text-[8px] px-1 rounded">{t}</span>
                        ))}
                      </div>
                    )}

                    {/* Sub-tag input */}
                    <div className="px-1 py-1 bg-gray-50">
                      <input
                        type="text"
                        value={(img.tags || []).join(', ')}
                        onChange={(e) => {
                          const newImages = [...currentCollection.images];
                          newImages[index] = { ...newImages[index], tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) };
                          setCurrentCollection({ ...currentCollection, images: newImages });
                        }}
                        className="w-full px-1 py-0.5 text-[10px] border rounded"
                        placeholder="Tags..."
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    
                    {/* Controls overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto" style={{bottom: '30px'}}>
                      <button
                        onClick={() => moveImage(index, 'up')}
                        disabled={index === 0}
                        className="p-1.5 bg-white rounded-full shadow disabled:opacity-30"
                        title="Move up"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveImage(index, 'down')}
                        disabled={index === currentCollection.images.length - 1}
                        className="p-1.5 bg-white rounded-full shadow disabled:opacity-30"
                        title="Move down"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleRemoveImage(index)}
                        className="p-1.5 bg-red-500 text-white rounded-full shadow"
                        title="Remove"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleImageUpload(e, false)}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
            />
            {uploading && <p className="text-sm text-gray-500 mt-2">Uploading...</p>}
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleSave}
              disabled={uploading}
              className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:bg-gray-400"
            >
              Save Collection
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Portfolio Collections</h2>
        <button
          onClick={handleNew}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
        >
          + New Collection
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {collections.map((collection) => (
          <div key={collection.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            {collection.cover_image_url && (
              <img
                src={collection.cover_image_url}
                alt={collection.couple}
                className="w-full h-48 object-cover"
                style={{ 
                  objectPosition: collection.cover_focal_point 
                    ? `${collection.cover_focal_point.x}% ${collection.cover_focal_point.y}%` 
                    : 'center' 
                }}
              />
            )}
            <div className="p-4">
              <h3 className="font-bold text-lg">{collection.couple}</h3>
              <p className="text-sm text-gray-600">{collection.location}</p>
              <p className="text-xs text-gray-500 mt-2">
                {Array.isArray(collection.images) ? collection.images.length : 0} images
              </p>
              {(collection.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {collection.tags.map((tag, i) => (
                    <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">{tag}</span>
                  ))}
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleEdit(collection)}
                  className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(collection.id)}
                  className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {collections.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No collections yet. Create your first one!
        </div>
      )}
    </div>
  );
};

export default CollectionsPanel;
